import { h } from 'preact';
import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import { viewerFile, fileKind, fileNameFromDisposition, saveName } from '../../utils/openFile.js';
import PdfPages from './PdfPages.jsx';

// Shows a file of the app in an overlay with a close button, in the installed app only
// (see utils/openFile.js). A photo or PDF is shown; anything else (Word, Excel, …) gets
// a card. "Opslaan of delen" opens the iOS share sheet: save to Files, open in Word, AirDrop.
// Mounted once in the Shell.
export default function FileViewer() {
  const file = viewerFile.value;
  if (!file) return null;
  return <FileViewerOverlay key={file.url} file={file} onClose={() => { viewerFile.value = null; }} />;
}

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}

function FileViewerOverlay({ file, onClose }) {
  const [loaded, setLoaded] = useState(null); // { blob, name, type, kind, objectUrl, data }
  const [failed, setFailed] = useState(false);
  const [shareFailed, setShareFailed] = useState(false);
  const close = useRef(null);

  useLayoutEffect(() => {
    const opener = document.activeElement;
    // Why restore the old value: the viewer can open on top of a Modal that locked scrolling too.
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    // Why: Android's back button changes the page underneath; the file shouldn't stay on top.
    window.addEventListener('popstate', onClose);
    close.current?.focus();
    return () => {
      document.body.style.overflow = overflow;
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('popstate', onClose);
      opener?.focus?.();
    };
  }, []);

  useEffect(() => {
    const abort = new AbortController();
    let objectUrl = null;
    (async () => {
      const res = await fetch(file.url, { credentials: 'same-origin', signal: abort.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const type = blob.type || res.headers.get('Content-Type') || 'application/octet-stream';
      const name = saveName(file.name, type, fileNameFromDisposition(res.headers.get('Content-Disposition')));
      const kind = fileKind(type);
      objectUrl = URL.createObjectURL(blob);
      const data = kind === 'pdf' ? await blob.arrayBuffer() : null;
      if (!abort.signal.aborted) setLoaded({ blob, name, type, kind, objectUrl, data });
    })().catch(err => {
      if (abort.signal.aborted) return;
      console.error('File could not be opened:', err);
      setFailed(true);
    });
    return () => {
      abort.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [file.url]);

  const shareFile = loaded && new File([loaded.blob], loaded.name, { type: loaded.type });
  const canShare = !!shareFile && !!navigator.canShare?.({ files: [shareFile] });

  async function share() {
    setShareFailed(false);
    try {
      await navigator.share({ files: [shareFile] });
    } catch (err) {
      // Why: AbortError is the user closing the share sheet, not a failure.
      if (err?.name !== 'AbortError') setShareFailed(true);
    }
  }

  const title = loaded?.name || file.name || 'Bestand';
  const saveButton = loaded && (canShare
    ? <button type="button" class="file-viewer-save" onClick={share}>Opslaan of delen</button>
    : <a class="file-viewer-save" href={loaded.objectUrl} download={loaded.name}>Downloaden</a>);

  return (
    <div class="file-viewer" role="dialog" aria-modal="true" aria-label={title}>
      <div class="file-viewer-bar">
        <span class="file-viewer-title">{title}</span>
        {loaded && loaded.kind !== 'other' && saveButton}
        <button ref={close} type="button" class="file-viewer-close" onClick={onClose} aria-label="Sluiten">&times;</button>
      </div>
      <div class="file-viewer-body" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
        {failed ? (
          <p class="file-viewer-message">Dit bestand kon niet worden geopend. Het bestaat niet meer, je hebt er geen toegang toe, of je bent offline.</p>
        ) : !loaded ? (
          <p class="file-viewer-message">Laden…</p>
        ) : loaded.kind === 'image' ? (
          <img src={loaded.objectUrl} alt={title} />
        ) : loaded.kind === 'pdf' ? (
          <PdfPages data={loaded.data} title={title} />
        ) : (
          <div class="file-viewer-card">
            <div class="file-viewer-card-icon" aria-hidden="true">📄</div>
            <div class="file-viewer-card-name">{loaded.name}</div>
            <div class="file-viewer-card-size">{formatSize(loaded.blob.size)}</div>
            {saveButton}
            {canShare && <p class="file-viewer-card-hint">Kies "Bewaar in Bestanden", of een app als Word of Excel om het te openen.</p>}
          </div>
        )}
        {shareFailed && <p class="file-viewer-message" role="alert">Delen lukte niet. Probeer het nog eens.</p>}
      </div>
    </div>
  );
}
